import type { components } from '@/generated/contracts/renderer-bridge';

// contracts/renderer-bridge.json: what the editor and its renderer post to each other
type Schemas = components['schemas'];

export type EditorToRendererMessage = Schemas['EditorToRendererMessage'];
export type RendererToEditorMessage = Schemas['RendererToEditorMessage'];
export type RendererMessageType = RendererToEditorMessage['type'];
export type RendererMessage<T extends RendererMessageType> = Extract<RendererToEditorMessage, { type: T }>;
export type TransformEdit = Schemas['TransformEdit'];
export type AssetInstanceTransformEdit = Schemas['AssetInstanceTransformEdit'];
export type CameraName = Schemas['CameraName'];

type FieldsOf<T extends RendererMessageType> = readonly Exclude<keyof RendererMessage<T>, 'type'>[];

/** Each renderer message's required fields besides `type`, as the contract lists them (test:renderer-bridge checks). */
export const RENDERER_MESSAGE_FIELDS = {
  requestJwt: [],
  rendererLoaded: ['version'],
  rendererError: ['message'],
  pointerDown: [],
  keyboardEvent: ['key', 'code', 'ctrlKey', 'shiftKey', 'altKey', 'metaKey'],
  canvasReadyResponse: ['requestId', 'ready'],
  screenshotResponse: ['requestId', 'success'],
  sceneLoaded: ['sceneName'],
  assetInstanceLoaded: ['assetInstanceUniqueId'],
  assetInstanceSelected: ['assetInstanceUniqueId'],
  selectionCleared: [],
  keyframeSelected: ['keyframeUniqueId', 'assetInstanceUniqueId'],
  cameraToggled: ['cameraName'],
  assetInstanceTransformsEdited: ['edits', 'cause'],
  keyframeTransformsEdited: ['edits', 'cause'],
  assetInstanceRequested: ['request'],
  clipTypesDiscovered: ['request'],
} as const satisfies { [T in RendererMessageType]: FieldsOf<T> };

/** The renderer message in `data`, or null when it is not one: an unknown type or a required field missing. */
export function parseRendererMessage(data: unknown): RendererToEditorMessage | null {
  if (!data || typeof data !== 'object') return null;
  const message = data as Record<string, unknown>;
  const type = message.type;
  if (typeof type !== 'string' || !Object.prototype.hasOwnProperty.call(RENDERER_MESSAGE_FIELDS, type)) return null;
  const required: readonly string[] = RENDERER_MESSAGE_FIELDS[type as RendererMessageType];
  if (required.some(field => message[field] === undefined || message[field] === null)) return null;
  return message as RendererToEditorMessage;
}
